import { Component, EventEmitter, Input, OnInit, Output, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { LoaderComponent } from '../loader/loader.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'app-manage-committees-modal',
  standalone: true,
  imports: [CommonModule, LoaderComponent, SpinnerComponent],
  template: `
    <div class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4 bg-gray-900/60 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-labelledby="manageCommitteesTitle">
      <div class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full flex flex-col max-h-[92dvh] sm:max-h-[85vh]">
        <div class="sm:hidden mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300 flex-shrink-0"></div>
        <!-- Header -->
        <div class="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-gray-100">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 id="manageCommitteesTitle" class="text-lg font-bold text-gray-900">Manage committees</h2>
              <p class="text-xs text-gray-500 mt-0.5">Pick every committee this member belongs to. At least one is required.</p>
            </div>
            <button type="button" (click)="close.emit()"
                    class="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Committees list -->
        <div class="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-2">
          @if (loading()) {
            <app-loader caption="Loading committees…"></app-loader>
          } @else if (units().length === 0) {
            <div class="py-6 text-center text-sm text-gray-500">No active committees available.</div>
          } @else {
            @for (unit of units(); track unit.id) {
              <div class="flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors"
                   [class.border-blue-300]="isSelected(unit.id)"
                   [class.bg-blue-50]="isSelected(unit.id)"
                   [class.border-gray-200]="!isSelected(unit.id)"
                   [class.hover:bg-gray-50]="!isSelected(unit.id)"
                   (click)="toggle(unit.id)">
                <div class="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                     [class.bg-blue-600]="isSelected(unit.id)"
                     [class.border-blue-600]="isSelected(unit.id)"
                     [class.border-gray-300]="!isSelected(unit.id)">
                  @if (isSelected(unit.id)) {
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  }
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ unit.name }}</p>
                  @if (unit.description) {
                    <p class="text-xs text-gray-500 truncate">{{ unit.description }}</p>
                  }
                </div>
              </div>
            }
          }
        </div>

        <!-- Footer -->
        <div class="px-4 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button type="button" (click)="close.emit()"
                  class="w-full sm:w-auto max-md:tap-target px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            Cancel
          </button>
          <button type="button" (click)="onSave()"
                  [disabled]="!dirty() || saving() || selectedIds().size === 0"
                  class="w-full sm:w-auto max-md:tap-target inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300
                         disabled:cursor-not-allowed text-white rounded-lg transition-colors">
            @if (saving()) { <app-spinner size="xs" colour="white"></app-spinner> }
            {{ saving() ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class ManageCommitteesModalComponent implements OnInit {
  @Input({ required: true }) currentBusinessUnitIds: string[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<string[]>();

  private buStore = inject(BusinessUnitStore);

  readonly units = this.buStore.activeUnits;
  readonly loading = this.buStore.loading;

  saving = signal(false);
  selectedIds = signal<Set<string>>(new Set());
  private initialIds = signal<Set<string>>(new Set());

  dirty = computed(() => {
    const a = this.selectedIds();
    const b = this.initialIds();
    if (a.size !== b.size) return true;
    for (const id of a) if (!b.has(id)) return true;
    return false;
  });

  ngOnInit(): void {
    this.buStore.loadOnce();
    const initial = new Set(this.currentBusinessUnitIds ?? []);
    this.selectedIds.set(new Set(initial));
    this.initialIds.set(initial);
  }

  isSelected(id: string): boolean { return this.selectedIds().has(id); }

  toggle(id: string): void {
    const next = new Set(this.selectedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selectedIds.set(next);
  }

  onSave(): void {
    if (!this.dirty() || this.saving() || this.selectedIds().size === 0) return;
    this.saving.set(true);
    this.save.emit(Array.from(this.selectedIds()));
  }
}
